import { useUpdateResidentDietCategoriesMutation } from "app/services/api/residentsApi";
import { useGetDietCategoriesQuery } from "app/services/api/categoriesApi";
import { useResidentContext } from "../provider/ResidentProvider";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import {
  CreateResidentDietRequest,
  DietCategorySelection,
} from "app/services/api/types/residents/requests/CreateResidentDietRequest";
import { useForm } from "react-hook-form";
import { handleBackendError } from "ui/utils/Errors";
import { ResidentDietForm } from "app/services/api/types/residents/models/AddResidentDietform";

export const useHandleResidentDietaryOptions = () => {
  const { residentId, resident } = useResidentContext();
  const { showNewToast, showErrorToast } = useDisplayToast();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data: dietData, isLoading: areCategoriesLoading } =
    useGetDietCategoriesQuery(undefined);

  const [triggerUpdateResidentDietCategories] =
    useUpdateResidentDietCategoriesMutation();

  const {
    control,
    handleSubmit,
    setError,
    reset,
    formState: { isSubmitting, isDirty },
  } = useForm<ResidentDietForm>({
    defaultValues: {
      selectedSolidFood: [],
      selectedLiquidFood: [],
      selectedRequiresDescription: [],
    },
  });

  const categorizedDietOptions = useMemo(() => {
    const solidFood: { label: string; value: number }[] = [];
    const liquidFood: { label: string; value: number }[] = [];
    const requiresDescription: { label: string; value: number }[] = [];

    dietData?.forEach((diet) => {
      if (diet.requires_description) {
        requiresDescription.push({ label: diet.name, value: diet.id });
      } else if (diet.children?.length) {
        diet.children.forEach((child) =>
          liquidFood.push({ label: child.name, value: child.id }),
        );
      } else {
        solidFood.push({ label: diet.name, value: diet.id });
      }
    });

    return {
      solidFoodOptions: solidFood,
      liquidFoodOptions: liquidFood,
      requiresDescriptionOptions: requiresDescription,
    };
  }, [dietData]);

  const { solidFoodOptions, liquidFoodOptions, requiresDescriptionOptions } =
    categorizedDietOptions;

  const updateFormValues = useCallback(() => {
    if (resident?.diet_categories) {
      const getSelectedValues = (options: { value: number }[]) =>
        resident?.diet_categories
          .filter((item) =>
            options.some((opt) => opt.value === item.diet_category.id),
          )
          .map((item) => item.diet_category.id);

      reset({
        selectedSolidFood: getSelectedValues(solidFoodOptions),
        selectedLiquidFood: getSelectedValues(liquidFoodOptions),
        selectedRequiresDescription: resident.diet_categories
          .filter((item) =>
            requiresDescriptionOptions.some(
              (opt) => opt.value === item.diet_category.id,
            ),
          )
          .map((item) => ({
            diet_category_id: item.diet_category.id,
            description: item.description || "",
          })),
      });
    } else {
      reset({
        selectedSolidFood: [],
        selectedLiquidFood: [],
        selectedRequiresDescription: [],
      });
    }
  }, [
    resident,
    reset,
    solidFoodOptions,
    liquidFoodOptions,
    requiresDescriptionOptions,
  ]);

  useEffect(() => {
    updateFormValues();
  }, [updateFormValues]);

  const submitForm = () => {
    return new Promise<boolean>((resolve) => {
      if (isSubmitting) {
        resolve(false);
        return;
      }
      handleSubmit(
        async (data: ResidentDietForm) => {
          try {
            if (!residentId) {
              showErrorToast(
                `Resident ID is not available. ${residentId} ${resident?.id}`,
              );
              resolve(false);
              return;
            }
            if (!isDirty) {
              resolve(true);
              return;
            }
            const transformedData: CreateResidentDietRequest = {
              diet_categories: [
                ...data.selectedSolidFood.map(
                  (id): DietCategorySelection => ({ diet_category_id: id }),
                ),
                ...data.selectedLiquidFood.map(
                  (id): DietCategorySelection => ({ diet_category_id: id }),
                ),
                ...data.selectedRequiresDescription.map(
                  ({
                    diet_category_id, // eslint-disable-line camelcase
                    description,
                  }): DietCategorySelection => ({
                    diet_category_id, // eslint-disable-line camelcase
                    description,
                  }),
                ),
              ],
            };

            await triggerUpdateResidentDietCategories({
              residentId,
              body: transformedData,
            }).unwrap();

            showNewToast({
              title: "Diet Categories Updated",
              message: "Successfully updated resident's diet categories.",
              type: "success",
            });

            resolve(true);
          } catch (e) {
            const result = handleBackendError(e, setError, setErrorMessage);
            showErrorToast(e);
            resolve(result);
          }
        },
        (errors) => {
          console.error("Form errors:", errors);
          resolve(false);
        },
      )();
    });
  };

  return {
    errorMessage,
    control,
    solidFoodOptions,
    liquidFoodOptions,
    requiresDescriptionOptions,
    areCategoriesLoading,
    submitForm,
  };
};
